import { Dropdown } from '@acko-tech/building-blocks.ui.dropdown';
import { Input } from '@acko-tech/building-blocks.ui.input';
import { useContext, useEffect, useState } from 'react';
import Header from 'widgets/header';
import * as Styles from './styles';
import {
    GSTDetailsFormState,
    gstNumberRegex,
    GSTTypes,
    IGSTData,
    IGstDropDown,
} from './types';
import DocumentUploadWidget from 'widgets/documentUpload';
import { getDomainOrigin } from 'utils/serverConfig';
import { newPlanUrls } from 'utils/api/urls';
import { useRouter } from 'next/router';
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button';
import { useViewport } from 'utils/responsive';
import { ModalMobile, ModalDesktop } from '@acko-tech/building-blocks.ui.modal';
import GSTLoading from './loading';
import { PageLoader } from 'utils/styles';
import { getImageBasePath } from 'utils/generics';
import GSTCompleted from './gstCompleted';
import GSTFailed from './gstFailed';
import {
    fetchCurrentGSTDetailsOfUser,
    submitGSTDetailsOfUser,
} from 'utils/api/services';
import usePermissionHook from 'hooks/usePermissionHook';
import SnackBar from 'library/snackbar';
import { ISubmitGSTDetailsRequest } from 'utils/api/types';
import { MPosContext } from 'utils/context';

const GSTDetails = () => {
    const { getPartnerIdFromPermission } = usePermissionHook();
    const { setGstDetailsValid, permissions, userDetails } =
        useContext(MPosContext);

    const [gstData, setGstData] = useState<IGSTData>({
        dealershipName: '',
        gstNumber: '',
        gstType: '',
        panNumber: '',
        panUrl: '',
    });

    const [dealershipName, setDealershipName] = useState('');
    const [gstNumber, setGstNumber] = useState('');
    const [gstType, setGstType] = useState<IGstDropDown>({
        displayValue: '',
        value: '',
    });
    const [panNumber, setPanNumber] = useState('');
    const [panFileUpload, setPanFileUpload] = useState({
        name: '',
        uploadStatus: '',
        url: '',
    });
    const [validationErr, setValidationErr] = useState({
        dealershipName: '',
        gstType: '',
        gstNumber: '',
        panNumber: '',
        panFileUpload: '',
    });
    const [snackbar, setSnackbar] = useState({
        type: '',
        message: '',
    });

    const [submitButtonEnabled, setSubmitButtonEnabled] = useState(false);
    const [state, setState] = useState<GSTDetailsFormState>(
        GSTDetailsFormState.INITATED
    );
    const [processing, setProcessing] = useState(false);
    const [pageLoading, setPageLoading] = useState(false);

    const router = useRouter();
    const { isMobile } = useViewport();

    useEffect(() => {
        function getButtonDisability() {
            let isError = false;
            if (dealershipName.length === 0) {
                setSubmitButtonEnabled(false);
                isError = true;
            }
            if (gstType.value.length === 0) {
                setSubmitButtonEnabled(false);
                isError = true;
            }
            if (gstNumber.length > 0) {
                if (gstNumber.length === 15 && gstNumberRegex.test(gstNumber)) {
                    setPanNumber(gstNumber.substring(2, 12));
                    setValidationErr((validationErr) => ({
                        ...validationErr,
                        gstNumber: '',
                    }));
                } else {
                    isError = true;
                    setValidationErr((validationErr) => ({
                        ...validationErr,
                        gstNumber: 'GST Number is mandatory',
                    }));
                    setPanNumber('');
                }
            } else {
                isError = true;
                setSubmitButtonEnabled(false);
            }
            if (panFileUpload.url.length === 0) {
                isError = true;
                setSubmitButtonEnabled(false);
            }
            if (isError) {
                setSubmitButtonEnabled(false);
                return;
            }
            setSubmitButtonEnabled(true);
        }
        getButtonDisability();
    }, [gstNumber, dealershipName, gstType, panFileUpload]);

    useEffect(() => {
        const fetchCurrentGSTDetails = async () => {
            setPageLoading(true);
            const response = await fetchCurrentGSTDetailsOfUser(
                getPartnerIdFromPermission(),
                permissions.group
            );
            if (response.is_error) {
                setSnackbar({
                    type: 'failure',
                    message:
                        response.error_message ??
                        'Failed to fetch current GST Details',
                });
            } else {
                if (response.data) {
                    const { gst_details, pan_details } = response.data;
                    setGstData((gstData) => ({
                        ...gstData,
                        dealershipName: gst_details.dealership_name,
                        gstNumber: gst_details.number,
                        gstType: gst_details.type,
                        panNumber: pan_details.number,
                        panUrl: pan_details.image_url,
                    }));
                }
                setSnackbar({
                    type: '',
                    message: '',
                });
            }
            setPageLoading(false);
        };
        fetchCurrentGSTDetails();
    }, []);

    const onChangeGSTType = (e: IGstDropDown) => {
        setGstType({ ...e });
    };

    const onChangeGSTNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setGstNumber(value);
    };

    const onSelectFile = (id: string, file: File) => {
        setPanFileUpload((fileUpload) => {
            return {
                ...fileUpload,
                name: id,
                uploadStatus: 'uploading',
            };
        });
        import('utils/api/services').then(async (req) => {
            const url = `${getDomainOrigin()}/${newPlanUrls.documentUpload}`;
            const { data, statusCode, status } = await req.uploadDocument(
                url,
                file,
                'PAN_IMAGE',
                userDetails.user_id,
                () => {}
            );
            if (statusCode === 401) {
                router.push('/login');
                return;
            }
            if (status && data) {
                setPanFileUpload((fileUpload) => {
                    return {
                        ...fileUpload,
                        uploadStatus: 'uploaded',
                        url: data.url,
                    };
                });
                return;
            }
            setPanFileUpload((fileUpload) => {
                return {
                    ...fileUpload,
                    uploadStatus: 'failed',
                };
            });
        });
    };

    const onDeleteFile = () => {
        setPanFileUpload((fileUpload) => {
            return {
                ...fileUpload,
                name: '',
                uploadStatus: '',
            };
        });
    };

    const onSubmitHandler = async () => {
        let isError = false;
        let updatedValidationErr = {
            ...validationErr,
        };
        if (dealershipName.length === 0) {
            isError = true;
            updatedValidationErr = {
                ...updatedValidationErr,
                dealershipName: 'Dealership name is mandatory',
            };
        }
        if (gstType.value.length === 0) {
            isError = true;
            updatedValidationErr = {
                ...updatedValidationErr,
                gstType: 'GST Type is mandatory',
            };
        }
        if (gstNumber.length > 0) {
            if (gstNumber.length === 15 && gstNumberRegex.test(gstNumber)) {
                updatedValidationErr = {
                    ...updatedValidationErr,
                    gstNumber: '',
                };
            } else {
                isError = true;
                updatedValidationErr = {
                    ...updatedValidationErr,
                    gstNumber: 'GST Number is mandatory',
                };
            }
        } else {
            isError = true;
            updatedValidationErr = {
                ...updatedValidationErr,
                gstNumber: 'GST Number is mandatory',
            };
        }
        if (panFileUpload.url.length === 0) {
            isError = true;
            updatedValidationErr = {
                ...updatedValidationErr,
                panFileUpload: 'Pan Image is mandatory',
            };
        }
        if (isError) {
            setValidationErr({ ...updatedValidationErr });
            return;
        }

        setProcessing(true);
        const request: ISubmitGSTDetailsRequest = {
            gst_details: {
                dealership_name: dealershipName,
                number: gstNumber,
                type: gstType.value,
            },
            pan_details: {
                image_url: panFileUpload.url,
                number: panNumber,
            },
        };
        const response = await submitGSTDetailsOfUser(
            getPartnerIdFromPermission(),
            permissions.group,
            request
        );
        if (response.is_error) {
            setSnackbar({
                type: 'failure',
                message:
                    response.error_message ??
                    'Failed to fetch current GST Details',
            });
            setState(GSTDetailsFormState.FAILED);
            setProcessing(false);

            return;
        }
        if (response.data) {
            const { success } = response.data;
            if (success) {
                setState(GSTDetailsFormState.COMPLETED);
                setGstDetailsValid(true);
            } else {
                setState(GSTDetailsFormState.FAILED);
                setSnackbar({
                    type: 'failure',
                    message:
                        response.error_message ??
                        'Failed to submit GST Details',
                });
            }
        } else {
            setSnackbar({
                type: 'failure',
                message:
                    response.error_message ?? 'Failed to submit GST Details',
            });
            setState(GSTDetailsFormState.FAILED);
        }
    };

    const Modal: any = isMobile ? ModalMobile : ModalDesktop;

    if (pageLoading) {
        return <PageLoader src={getImageBasePath('/gif/pageLoader.gif')} />;
    }

    return (
        <>
            <Styles.Wrapper>
                <Header />
                <Styles.Content>
                    {state === GSTDetailsFormState.COMPLETED ? (
                        <Styles.Section $index={0}>
                            <GSTCompleted />
                        </Styles.Section>
                    ) : state === GSTDetailsFormState.FAILED ? (
                        <Styles.Section $index={0}>
                            <GSTFailed />
                        </Styles.Section>
                    ) : (
                        <>
                            <Styles.Section $index={0}>
                                <Styles.EnterMoreDetails>
                                    Update your GST to unlock transactions
                                </Styles.EnterMoreDetails>
                            </Styles.Section>
                            <Styles.Section $index={1}>
                                <Styles.Details>Current Details</Styles.Details>
                                <Styles.StaticWrapper>
                                    <Styles.InputLabel>
                                        Dealership Name
                                    </Styles.InputLabel>
                                    <Input
                                        className=""
                                        name="current_dealership_name"
                                        type={'text'}
                                        value={gstData.dealershipName}
                                        disabled={true}
                                        onChange={() => {}}
                                    />
                                </Styles.StaticWrapper>
                                <Styles.StaticWrapper>
                                    <Styles.InputLabel>
                                        GST Type
                                    </Styles.InputLabel>
                                    <Input
                                        className=""
                                        name="current_gst_type"
                                        type={'text'}
                                        value={gstData.gstType}
                                        disabled={true}
                                        onChange={() => {}}
                                    />
                                </Styles.StaticWrapper>

                                <Styles.StaticWrapper>
                                    <Styles.InputLabel>
                                        GST Number
                                    </Styles.InputLabel>
                                    <Input
                                        className=""
                                        name="current_gst_number"
                                        type={'text'}
                                        value={gstData.gstNumber}
                                        disabled={true}
                                        onChange={() => {}}
                                    />
                                </Styles.StaticWrapper>
                                <Styles.StaticWrapper>
                                    <Styles.InputLabel>
                                        Pan Number
                                    </Styles.InputLabel>
                                    <Input
                                        className=""
                                        name="current_pan_number"
                                        type={'text'}
                                        value={gstData.panNumber}
                                        disabled={true}
                                        onChange={() => {}}
                                    />
                                </Styles.StaticWrapper>
                            </Styles.Section>
                            <Styles.Section $index={2}>
                                <Styles.Details>To Be Updated</Styles.Details>
                                <Styles.StaticWrapper>
                                    <Styles.InputLabel>
                                        Dealership Name
                                        <Styles.MandatoryStar>
                                            *
                                        </Styles.MandatoryStar>
                                    </Styles.InputLabel>
                                    <Input
                                        className=""
                                        name={'dealershipName'}
                                        placeHolder={'Dealership Name'}
                                        type={'text'}
                                        value={dealershipName}
                                        onChange={(e) =>
                                            setDealershipName(e.target.value)
                                        }
                                        hasError={
                                            validationErr.dealershipName
                                                .length > 0
                                        }
                                        errMsg={validationErr.dealershipName}
                                    />
                                </Styles.StaticWrapper>
                                <Styles.StaticWrapper>
                                    <Styles.InputLabel>
                                        GST Type
                                        <Styles.MandatoryStar>
                                            *
                                        </Styles.MandatoryStar>
                                    </Styles.InputLabel>
                                    <Dropdown
                                        value={gstType}
                                        heading={'Select GST Type'}
                                        label={`GST Type`}
                                        options={GSTTypes}
                                        onChange={onChangeGSTType}
                                        error={validationErr.gstType}
                                    />
                                </Styles.StaticWrapper>

                                <Styles.StaticWrapper>
                                    <Styles.InputLabel>
                                        GST Number
                                        <Styles.MandatoryStar>
                                            *
                                        </Styles.MandatoryStar>
                                    </Styles.InputLabel>
                                    <Input
                                        className=""
                                        name={'gstNumber'}
                                        placeHolder={`We'll validate this with GST authorities`}
                                        type={'text'}
                                        value={gstNumber}
                                        onChange={onChangeGSTNumber}
                                        errMsg={validationErr.gstNumber}
                                        hasError={
                                            validationErr.gstNumber.length > 0
                                        }
                                    />
                                </Styles.StaticWrapper>

                                <Styles.StaticWrapper>
                                    <Styles.InputLabel>
                                        PAN Number
                                        <Styles.MandatoryStar>
                                            *
                                        </Styles.MandatoryStar>
                                    </Styles.InputLabel>
                                    <Input
                                        className=""
                                        name={'panNumber'}
                                        placeHolder={`Auto-filled based on the GST number`}
                                        type={'text'}
                                        value={panNumber}
                                        disabled={true}
                                        onChange={() => {}}
                                    />
                                </Styles.StaticWrapper>
                                <Styles.StaticWrapper>
                                    <DocumentUploadWidget
                                        id={'pan'}
                                        label={'Upload PAN card photo (front)'}
                                        title={'PAN Image'}
                                        documentName={panFileUpload.name}
                                        uploadStatus={
                                            panFileUpload.uploadStatus
                                        }
                                        isMandatory={true}
                                        onSelect={onSelectFile}
                                        onDelete={onDeleteFile}
                                    />
                                    {validationErr.panFileUpload.length > 0 && (
                                        <Styles.DocumentUploadErrorMessage>
                                            {validationErr.panFileUpload}
                                        </Styles.DocumentUploadErrorMessage>
                                    )}
                                </Styles.StaticWrapper>
                                <Styles.StaticWrapper>
                                    <PrimaryButton
                                        onClick={onSubmitHandler}
                                        disabled={!submitButtonEnabled}
                                    >
                                        Submit
                                    </PrimaryButton>
                                </Styles.StaticWrapper>
                                <Modal
                                    show={processing}
                                    urlhash={''}
                                    onHide={() => {}}
                                    padding={'20px'}
                                    maxWidth={'500px'}
                                    wrapperStyles={`border-radius: 16px 16px 0px 0px;`}
                                    showOverlayBackground={true}
                                    hideCloseButton={true}
                                >
                                    <GSTLoading />
                                </Modal>
                            </Styles.Section>
                        </>
                    )}
                </Styles.Content>
            </Styles.Wrapper>
            <SnackBar
                {...snackbar}
                handleSnakbarComplete={() =>
                    setSnackbar({ type: '', message: '' })
                }
            />
        </>
    );
};

export default GSTDetails;
